import { Button, ButtonGroup, Grid } from '@material-ui/core';
import React, { useState } from 'react';
import useStyles from './Graph.style';
import { costFormatter2 } from '../../../utils/config';
import { currencies } from '../../../utils/constants';
import { echartsColorPalette } from '../../HealthMonitoring/config';
import ChartCard from './ChartCard';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Box } from '@mui/material';
import { MultilevelHeaderTable } from '../../../components/CostBreakup/MultilevelHeaderTable';

export type ViewType = 'table' | 'barchart' | 'linechart';

interface DailyUsageBreakupProps {
    dailyUsageData: Record<string, any>;
    isSubmitting: boolean;
    activeView: ViewType;
    onViewChange: (view: ViewType) => void;
}

const DailyUsageBreakup: React.FC<DailyUsageBreakupProps> = ({
  dailyUsageData,
  isSubmitting,
  activeView,
  onViewChange
}) => {
    const classes = useStyles();
    const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [sortColumn, setSortColumn] = useState<string>('');
    
    // Handle chart initialization
    const handleChartInit = (chart: echarts.ECharts | null) => {
        setChartInstance(chart);
    };

    // Handle sorting
    const handleSort = (column: string) => {
        const newDirection =
        sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

        setSortColumn(column);
        setSortDirection(newDirection);
    };

   const dates = [
    '1-Jun', '2-Jun', '3-Jun', '4-Jun', '5-Jun', '6-Jun', '7-Jun',
    '8-Jun', '9-Jun', '10-Jun', '11-Jun', '12-Jun', '13-Jun',
    '14-Jun', '15-Jun', '16-Jun', '17-Jun', '18-Jun', '19-Jun',
    '20-Jun', '21-Jun'
    ];
    const columns = [
    { key: 'service', label: 'Services' },
    ...dates.map(date => ({ key: date, label: date })),
    { key: 'TotalCost', label: 'Total Cost' }
    ];
    const summableColumns = [...dates, 'TotalCost'];

    const generateColors = (count: number) => echartsColorPalette.slice(0, count);
    const getChartOptions = (data: Record<string, any>, view: ViewType): echarts.EChartsOption => {
        if (!data || Object.keys(data).length === 0) return {};

        const legend = Object.keys(data);
        const dates = Object.keys(data[legend[0]]).filter((key) => key !== 'totalCost');
        const colors = generateColors(legend.length);

        const series = legend.map((service: any, index: number) => ({
            name: service,
            type: view === 'linechart' ? 'line' as const : 'bar' as const,
            stack: 'total',
            data: dates.map((date: any) => data[service][date]?.cost || 0),
            itemStyle: {
            color: colors[index],
            },
        }));

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                confine: false,
                enterable: true,
                formatter: (params: any) => {
                    const header = `<div class="echarts-tooltip-title">${params[0].axisValue}</div>`;
                    const items = params
                    .map((param: any) => {
                        const colorSpan = `<span class="echarts-tooltip-color" style="background-color: ${param.color}"></span>`;
                        return `${colorSpan} <span class="echarts-tooltip-name">${param.seriesName} :</span> <span class="echarts-tooltip-value">${costFormatter2(currencies.USD.shortHand, param.value)}</span>`;
                    })
                    .join('<br/>');
                    return `<div class="echarts-tooltip">${header}${items}</div>`;
                },
            },
            legend: { show: false },
            grid: {
                left: '1%',
                right: '2%',
                bottom: '4%',
                top: '5%',
                containLabel: true,
            },
            dataZoom: [{ type: 'inside', show: true }],
            xAxis: { type: 'category', data: dates },
            yAxis: { type: 'value', axisLabel: { formatter: '${value}' } },
            series: series,
        };
    };

    const transformDailyUsageDataForTable = (rawData: Record<string, any>): [string, any][] => {
        return Object.entries(rawData).map(([serviceName, serviceData]) => {
            const row: Record<string, any> = { service: serviceName };

            Object.entries(serviceData).forEach(([dateOrTotal, value]) => {
            if (value && typeof value === 'object' && 'cost' in value) {
                row[dateOrTotal] = (value as any)?.cost ?? 0;
            } else {
                row[dateOrTotal] = value ?? 0;
            }
            });

            return [serviceName, row] as [string, any];
        });
    };
                    
    return (
        <div className="graph-container">
            <Grid container>
                <Grid item xs={12} >
                <Box className={classes.headerWrapper} display="flex" justifyContent="space-between" alignItems="center">
                    <Grid item className={classes.chartTitle}>
                        Daily Usage Breakup - Service Wise (in GB)
                    </Grid>
                    <Grid item>
                        <ButtonGroup className="viewToggleGroup">
                            <Button
                            className={`timeframeButton ${activeView === 'barchart' ? 'timeframeActive' : ''}`}
                            onClick={() => onViewChange('barchart')}
                            >
                            <BarChartOutlinedIcon />
                            </Button>
                            <Button
                            className={`timeframeButton ${activeView === 'table' ? 'timeframeActive' : ''}`}
                            onClick={() => onViewChange('table')}
                            >
                            <TableChartOutlinedIcon />
                            </Button>
                            <Button
                            className={`timeframeButton ${activeView === 'linechart' ? 'timeframeActive' : ''}`}
                            onClick={() => onViewChange('linechart')}
                            >
                            <TimelineIcon />
                            </Button>
                        </ButtonGroup>
                    </Grid>
                </Box>

                {/* Switch between chart and table */}
                {activeView === 'table' ? (
                    <div style={{ padding: '2rem', border: '1px solid #e0e0e0' }}>
                       <MultilevelHeaderTable
                        loading={isSubmitting}
                        sortedEntries={
                            dailyUsageData && Object.keys(dailyUsageData).length > 0
                                ? transformDailyUsageDataForTable(dailyUsageData)
                                : []
                        }
                        columns={columns}
                        summableColumns={summableColumns}
                        onSort={handleSort}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        />
                    </div>
                ) : (
                    <ChartCard
                    title=""
                    data={dailyUsageData}
                    isSubmitting={isSubmitting}
                    generateColors={generateColors}
                    getChartOptions={(data) => getChartOptions(data, activeView)}
                    onChartInit={handleChartInit}
                    chartInstance={chartInstance}
                    classes={classes}
                    legendHeading="Service"
                    hideHeader
                    />
                )}
                </Grid>
            </Grid>
        </div>
    );
};

export default DailyUsageBreakup;